import { BarChart3 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { OverviewCardRow } from "@/lib/dashboard/OverviewCardRow";
import { useI18n } from "@/hooks/useI18n";

interface OverviewStatsCardProps {
  /** Pratiche chiuse e ancora aperte: servono al tasso di conversione. */
  completedCount: number;
  activeCount: number;
  /**
   * Viaggiatori delle pratiche del perimetro, non chi ha prenotato: tre viaggi venduti a
   * tre persone da quattro partecipanti l'uno fanno dodici. `undefined` quando il DB non
   * ha ancora la migrazione che lo espone.
   */
  travelersCount: number | undefined;
  closedValueTotal: number;
  avgValuePerItinerary: number;
  marginTotal: number;
  avgMarginPercent: number;
  /** Fee di piattaforma: valorizzata solo per l'admin, `null` per tutti gli altri. */
  platformFeeTotal: number | null;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

/**
 * Le statistiche derivate della Panoramica.
 *
 * Stanno insieme e in fondo alla riga perche' sono numeri di secondo grado: rapporti e
 * medie, non fatti. Sparsi fra le tessere si leggevano come dati primari, e una media
 * accanto a un totale sembra un totale.
 *
 * Conversione e valore medio per viaggiatore si calcolano qui e non nella RPC: sono
 * divisioni fra numeri che la RPC gia' restituisce, e farli scendere dal server
 * significherebbe poterli far divergere da cio' che si vede.
 */
export function OverviewStatsCard({
  completedCount,
  activeCount,
  travelersCount,
  closedValueTotal,
  avgValuePerItinerary,
  marginTotal,
  avgMarginPercent,
  platformFeeTotal,
  formatCurrency,
  formatPercent,
}: OverviewStatsCardProps) {
  const { t } = useI18n();

  const totalRequests = completedCount + activeCount;
  // Senza pratiche il rapporto non e' zero, non esiste: uno "0%" si leggerebbe come "non
  // chiudiamo niente" invece che "non c'e' ancora niente da chiudere".
  const conversion =
    totalRequests > 0 ? formatPercent((completedCount / totalRequests) * 100) : "—";
  // Diviso i viaggiatori e non i clienti: chi prenota e' uno, chi parte sono quattro, e
  // sul numero di chi prenota la media raccontava un quarto del valore generato.
  const avgPerTraveler =
    travelersCount && travelersCount > 0
      ? formatCurrency(closedValueTotal / travelersCount)
      : "—";

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center h-8 w-8 rounded-lg flex-shrink-0 bg-sky-50 text-sky-600">
            <BarChart3 className="h-4 w-4" />
          </div>
          <span className="text-sm text-gray-500">
            {t("dashboard.overview.stats.title")}
            {" "}
            <InfoTooltip text={t("dashboard.overview.stats.info")} />
          </span>
        </div>

        <div>
          <div className="text-2xl font-semibold text-gray-900 tabular-nums">
            {formatCurrency(closedValueTotal)}
          </div>
          <span className="text-xs text-gray-500">
            {t("dashboard.overview.stats.closedValue")}
          </span>
        </div>

        <div className="flex flex-col gap-1.5 mt-auto pt-1 border-t border-gray-100">
          <OverviewCardRow
            label={t("dashboard.overview.stats.conversion")}
            value={conversion}
            info={t("dashboard.overview.stats.conversionInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.avgValuePerItinerary")}
            value={formatCurrency(avgValuePerItinerary)}
            info={t("dashboard.overview.avgValuePerItineraryInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.stats.avgPerTraveler")}
            value={avgPerTraveler}
            info={t("dashboard.overview.stats.avgPerTravelerInfo")}
          />

          {/* Margine e media margine sono il proprio guadagno, non un dato di piattaforma:
              li vedono tutti i ruoli che aprono questa panoramica. La fee Place2Be resta
              all'amministrazione, e la regola di visibilita' vive in un posto solo -- la
              RPC la restituisce `null` a chi non la puo' vedere. */}
          <div className="flex flex-col gap-1.5 pt-1.5 border-t border-gray-100">
            <OverviewCardRow
              label={t("dashboard.overview.marginTotal")}
              value={formatCurrency(marginTotal)}
              info={t("dashboard.overview.marginTotalInfo")}
              strong
            />
            <OverviewCardRow
              label={t("dashboard.overview.avgMarginPercent")}
              value={formatPercent(avgMarginPercent)}
              info={t("dashboard.overview.avgMarginPercentInfo")}
            />
            {platformFeeTotal != null && (
              <OverviewCardRow
                label={t("dashboard.overview.platformFeeTotal")}
                value={formatCurrency(platformFeeTotal)}
                info={t("dashboard.overview.platformFeeTotalInfo")}
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default OverviewStatsCard;
